import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SliderCarousel = ({ language = 'en' }) => {
  const [sliders, setSliders] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchSliders();
  }, []);

  useEffect(() => {
    if (sliders.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % sliders.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [sliders.length]);

  const fetchSliders = async () => {
    try {
      const response = await axios.get(`${API}/sliders?active_only=true`);
      setSliders(response.data);
    } catch (error) {
      console.error('Error fetching sliders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % sliders.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + sliders.length) % sliders.length);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  if (isLoading) {
    return (
      <div className="h-48 flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (sliders.length === 0) {
    return null;
  }

  const currentSlider = sliders[currentIndex];
  // Always show Turkish content for slider regardless of page language
  const title = currentSlider.title_tr || currentSlider.title;
  const description = currentSlider.description_tr || currentSlider.description;

  return (
    <div className="relative group overflow-hidden">
      {/* Slider Content */}
      <div className="relative h-48 bg-gradient-to-r from-purple-600 via-blue-600 to-purple-700 overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center p-8">
          <div className="text-center text-white max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-3 drop-shadow-lg animate-fade-in">
              {title}
            </h2>
            <p className="text-lg opacity-90 drop-shadow-md">
              {description}
            </p>
            {currentIndex === 0 && (
              <div className="mt-4 flex items-center justify-center gap-2 animate-bounce-slow">
                <span className="text-xl">→</span>
                <span className="text-sm font-semibold">AI Assistant</span>
                <span className="text-xl">→</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}></div>
        </div>
      </div>

      {/* Navigation Arrows */}
      {sliders.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-2 rounded-full transition-all opacity-0 group-hover:opacity-100"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-2 rounded-full transition-all opacity-0 group-hover:opacity-100"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      {/* Dots Navigation */}
      {sliders.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
          {sliders.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex
                  ? 'bg-white w-8'
                  : 'bg-white/50 hover:bg-white/70'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SliderCarousel;